import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { useContractTemplateFields, useSetContractTemplateFields } from '../hooks/useContractTemplates';
import {
  CONTRACT_FIELD_KEYS,
  ContractFieldRequirement,
  ContractTemplateFieldKey,
  type ContractTemplate,
  type ContractTemplateFieldInput,
} from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  template: ContractTemplate | null;
}

type RowState = { included: boolean; label: string; requirement: ContractFieldRequirement };

const REQUIREMENT_TABS: { value: ContractFieldRequirement; label: string }[] = [
  { value: ContractFieldRequirement.REQUIRED, label: 'Required' },
  { value: ContractFieldRequirement.OPTIONAL, label: 'Optional' },
];

/**
 * Field-configuration editor. Each of watchman's eight ContractTemplateFieldKey values
 * can be included on this template version (Required/Optional) or left out. Onboarding
 * blocks assigning/signing until every Required field is resolved on the merchant record.
 * The backend PUT replaces the whole list, and 409s once the template has been signed —
 * we surface that as an error toast (handled in the hook) and keep the modal open.
 */
export function ContractTemplateFieldsModal({ isOpen, onClose, template }: Props) {
  const templateId = template?.id ?? null;
  const { data: existing, isLoading } = useContractTemplateFields(isOpen ? templateId : null);
  const setFields = useSetContractTemplateFields();

  const [rows, setRows] = useState<Record<ContractTemplateFieldKey, RowState>>(() => blankRows());

  // Seed the editor from the saved fields whenever the modal opens for a template.
  useEffect(() => {
    if (!isOpen) return;
    const next = blankRows();
    for (const f of existing ?? []) {
      if (next[f.fieldKey]) {
        next[f.fieldKey] = { included: true, label: f.label, requirement: f.requirement };
      }
    }
    setRows(next);
  }, [isOpen, existing]);

  const patch = (key: ContractTemplateFieldKey, p: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...p } }));

  const included = CONTRACT_FIELD_KEYS.filter(({ key }) => rows[key].included);
  const labelsOk = included.every(({ key }) => rows[key].label.trim().length > 0);
  const canSave = included.length > 0 && labelsOk && !setFields.isPending;

  const handleSave = async () => {
    if (!templateId || !canSave) return;
    const fields: ContractTemplateFieldInput[] = included.map(({ key }) => ({
      fieldKey: key,
      label: rows[key].label.trim(),
      requirement: rows[key].requirement,
    }));
    await setFields.mutateAsync({ id: templateId, data: { fields } });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-foreground">Configure fields</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <p className="text-[13px] text-muted-foreground mb-6">
          {template?.name} · v{template?.version} — choose which details a merchant must provide before
          this contract can be assigned. Required fields block signing until resolved.
        </p>

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading fields…</div>
        ) : (
          <div className="space-y-2">
            {CONTRACT_FIELD_KEYS.map(({ key, label }) => {
              const row = rows[key];
              return (
                <div
                  key={key}
                  className={`flex flex-wrap items-center gap-3 rounded-[12px] border px-4 py-3 transition-colors ${
                    row.included ? 'border-border bg-card' : 'border-dashed border-border bg-muted/40'
                  }`}
                >
                  <label className="flex items-center gap-2 min-w-[190px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={row.included}
                      onChange={(e) => patch(key, { included: e.target.checked })}
                      className="h-4 w-4 accent-primary"
                    />
                    <span className="text-[14px] font-[600] text-foreground">{label}</span>
                  </label>

                  <div className="flex-1 min-w-[180px]">
                    <Input
                      placeholder="Label shown to the merchant"
                      value={row.label}
                      disabled={!row.included}
                      onChange={(e) => patch(key, { label: e.target.value })}
                    />
                  </div>

                  <div className="inline-flex rounded-[10px] border border-border overflow-hidden">
                    {REQUIREMENT_TABS.map((tab) => (
                      <button
                        key={tab.value}
                        type="button"
                        disabled={!row.included}
                        onClick={() => patch(key, { requirement: tab.value })}
                        className={`px-3 py-1.5 text-[12px] font-[600] transition-colors disabled:opacity-40 ${
                          row.included && row.requirement === tab.value
                            ? 'bg-primary text-white'
                            : 'bg-card text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {included.length === 0 && !isLoading && (
          <p className="text-[13px] text-amber-600 mt-4">Include at least one field to save.</p>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" size="md" type="button" onClick={onClose} disabled={setFields.isPending}>
            Cancel
          </Button>
          <Button size="md" type="button" onClick={handleSave} loading={setFields.isPending} disabled={!canSave}>
            Save configuration
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function blankRows(): Record<ContractTemplateFieldKey, RowState> {
  return CONTRACT_FIELD_KEYS.reduce(
    (acc, { key, label }) => {
      acc[key] = { included: false, label, requirement: ContractFieldRequirement.REQUIRED };
      return acc;
    },
    {} as Record<ContractTemplateFieldKey, RowState>,
  );
}
